
import React, { useState } from 'react';
import { ExternalLink, ChevronDown, ChevronUp, AlertTriangle, Video } from 'lucide-react';
import { PROJECTS } from '../data';
import { Project } from '../types';

// Extracted Project Card for better state management per item
const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const [mediaError, setMediaError] = useState(false);
  
  const hasContent = project.title || project.description || (project.tags && project.tags.length > 0);
  
  // Helper to get YouTube Video ID
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const youtubeId = project.mediaType === 'video' ? getYouTubeId(project.mediaUrl) : null;

  return (
    <div 
      className="group relative bg-slate-900 rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-black/50 animate-in fade-in slide-in-from-bottom-8"
      style={{ animationDelay: `${(index % 6) * 100}ms`, animationFillMode: 'both' }}
    >
      {/* Media Container */}
      <div className={`relative w-full overflow-hidden bg-slate-950 ${hasContent ? 'aspect-video' : 'h-full min-h-[300px]'}`}>
          {mediaError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900 text-slate-500 border border-slate-800 p-4 text-center">
              <AlertTriangle className="w-10 h-10 mb-2 text-yellow-500" />
              <p className="text-sm font-bold text-slate-400">Failed to load media</p>
              <p className="text-xs mt-1 break-all opacity-70">{project.mediaUrl}</p>
              {project.mediaType === 'video' && (
                 <p className="text-[10px] mt-2 text-mc-blue">Check if file exists in public/</p>
              )}
            </div>
          ) : project.mediaType === 'video' ? (
          youtubeId ? (
              <iframe 
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
                  className="w-full h-full"
                  title={project.title || "Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
              ></iframe>
          ) : (
              <video 
                src={project.mediaUrl} 
                controls 
                playsInline
                muted
                className="w-full h-full object-cover bg-black"
                preload="metadata"
                onError={(e) => {
                  // Changed to warn to avoid filling console with errors for missing placeholder files
                  console.warn(`Video failed to load: ${project.mediaUrl}`);
                  setMediaError(true);
                }}
              >
                <div className="flex items-center justify-center h-full text-white">
                   <Video className="w-8 h-8 text-slate-500" />
                </div>
              </video>
          )
        ) : (
          <div className="w-full h-full relative">
            <img 
              src={project.mediaUrl} 
              alt={project.title || 'Project Preview'} 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              onError={() => setMediaError(true)}
            />
            {/* Subtle dark overlay on hover for focus */}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        )}

        {/* External Link Icon Overlay */}
        {project.linkUrl && (
            <a href={project.linkUrl} target="_blank" rel="noreferrer" className="absolute top-4 right-4 p-2.5 bg-slate-950/80 backdrop-blur-sm rounded-full text-white hover:bg-mc-blue hover:text-black transition-all opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 duration-300 z-20">
              <ExternalLink className="w-4 h-4" />
            </a>
        )}
      </div>

      {/* Content Container */}
      {hasContent && (
        <div className="p-6 flex flex-col">
          <div className="mb-4">
            {project.title && (
              <h3 className="text-lg font-bold text-white mb-2">
                {project.title}
              </h3>
            )}
            {project.description && (
              <p className="text-slate-400 text-sm leading-relaxed line-clamp-6">
                {project.description}
              </p>
            )}
          </div>
          
          {project.tags && project.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-auto">
              {project.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-[10px] uppercase font-bold tracking-wide border border-slate-700/50">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Portfolio: React.FC = () => {
  const [visibleCount, setVisibleCount] = useState(6);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 6);
  };

  const handleShowLess = () => {
    setVisibleCount((prev) => Math.max(6, prev - 6));
  };

  const visibleProjects = PROJECTS.slice(0, visibleCount);
  const hasMore = visibleCount < PROJECTS.length;
  const canShowLess = visibleCount > 6;

  return (
    <section id="portfolio" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
          <div>
            <h2 className="text-4xl font-extrabold text-white mb-2 tracking-tight">Showcase</h2>
            <p className="text-slate-500 text-lg max-w-2xl">
              These are mostly older custom projects filmed for this portfolio, featuring one active Spigot project I am currently improving. More Spigot projects will be added soon.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        {/* Pagination Buttons */}
        {(hasMore || canShowLess) && (
          <div className="mt-16 flex justify-center gap-4">
            {canShowLess && (
               <button 
                onClick={handleShowLess}
                className="group relative px-8 py-3 bg-slate-900 border border-slate-800 text-white font-bold rounded-full hover:bg-slate-800 hover:border-mc-blue/50 transition-all shadow-lg hover:shadow-mc-blue/10 flex items-center gap-2 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <ChevronUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" /> Show Less
                </span>
              </button>
            )}
            
            {hasMore && (
              <button 
                onClick={handleShowMore}
                className="group relative px-8 py-3 bg-slate-900 border border-slate-800 text-white font-bold rounded-full hover:bg-slate-800 hover:border-mc-blue/50 transition-all shadow-lg hover:shadow-mc-blue/10 flex items-center gap-2 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Show More Projects <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </span>
                {/* Button Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
